import * as React from "react";
import { Text } from "@blueprintjs/core";
import styled from "styled-components";
import type { ComponentProps } from "widgets/BaseComponent";
import Interweave from "interweave";
import { UrlMatcher, EmailMatcher } from "interweave-autolink";
import type { TextSize } from "constants/WidgetConstants";
import { DEFAULT_FONT_SIZE, FontStyleTypes } from "constants/WidgetConstants";
import { Icon, IconSize } from "@design-system/widgets-old";
import { get } from "lodash";
import equal from "fast-deep-equal/es6";
import ModalComponent from "components/designSystems/appsmith/ModalComponent";
import type { Color } from "constants/Colors";
import { Colors } from "constants/Colors";
import { fontSizeUtility, subStringText } from "widgets/WidgetUtils";
import { OverflowTypes } from "../constants";
import moment from "moment";

export type TextAlign = "LEFT" | "CENTER" | "RIGHT" | "JUSTIFY";

const ELLIPSIS_HEIGHT = 15;

export const TextContainer = styled.div<{
  fontFamily?: string;
}>`
  & {
    height: 100%;
    width: 100%;
    position: relative;
  }
  ul {
    list-style-type: disc;
    list-style-position: inside;
  }
  ol {
    list-style-type: decimal;
    list-style-position: inside;
  }
  ul ul,
  ol ul {
    list-style-type: circle;
    list-style-position: inside;
    margin-left: 15px;
  }
  ol ol,
  ul ol {
    list-style-type: lower-latin;
    list-style-position: inside;
    margin-left: 15px;
  }
  h1 {
    font-size: 2em;
    margin: 0.67em 0;
  }
  h2 {
    font-size: 1.5em;
    margin: 0.75em 0;
  }
  h3 {
    font-size: 1.17em;
    margin: 0.83em 0;
  }
  h5 {
    font-size: 0.83em;
    margin: 1.5em 0;
  }
  h6 {
    font-size: 0.75em;
    margin: 1.67em 0;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-weight: bold;
  }
  a {
    color: #106ba3;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }

  ${(props) =>
    props.fontFamily &&
    `
    font-family: ${props.fontFamily};
  `}
`;

const StyledIcon = styled(Icon) <{ backgroundColor?: string }>`
  cursor: pointer;
  bottom: 0;
  left: 0;
  right: 0;
  height: ${ELLIPSIS_HEIGHT}px;
  background: ${(props) =>
    props.backgroundColor ? props.backgroundColor : "transparent"};
`;

type StyledTextProps = React.PropsWithChildren<{
  overflow: OverflowTypes;
  isTruncated: boolean;
  textAlign: string;
  backgroundColor?: string;
  textColor?: string;
  fontStyle?: string;
  fontSize?: TextSize;
  lineHeight?: string;
}>;

export const StyledText = styled(Text) <StyledTextProps>`
  height: ${(props) =>
    props.overflow === OverflowTypes.TRUNCATE
      ? `calc(100% - ${ELLIPSIS_HEIGHT}px)`
      : "100%"};
  overflow-x: hidden;
  overflow-y: ${(props) =>
    props.overflow !== OverflowTypes.SCROLL ||
      props.overflow === OverflowTypes.TRUNCATE.valueOf()
      ? "hidden"
      : "auto"};
  text-overflow: ellipsis;
  && div {
    display: block;
    width: 100%;
  }
  display: flex;
  width: 100%;
  justify-content: flex-start;
  flex-direction: ${(props) => (props.isTruncated ? "column" : "unset")};
  align-items: ${(props) =>
    props.overflow === OverflowTypes.SCROLL ||
      props.overflow === OverflowTypes.TRUNCATE
      ? "flex-start"
      : "center"};
  background: ${(props) => props?.backgroundColor};
  color: ${(props) => props?.textColor};
  font-style: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.ITALIC) ? "italic" : ""};
  text-decoration: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.UNDERLINE) ? "underline" : ""};
  font-weight: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.BOLD) ? "bold" : "normal"};
  font-size: ${({ fontSize }) =>
    fontSizeUtility(fontSize) || DEFAULT_FONT_SIZE};
  word-break: break-word;
  span {
    width: 100%;
    line-height: ${(props) => props?.lineHeight};
    white-space: pre-wrap;
    text-align: ${(props) => props.textAlign.toLowerCase()};
  }
  .auto-layout & span {
    min-height: 32px;
  }
`;

const ModalContent = styled.div<{
  backgroundColor?: string;
}>`
  background: ${(props) => props?.backgroundColor || Colors.WHITE};
  padding: 24px;
  padding-top: 16px;

  a {
    color: #106ba3;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
`;

const Heading = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  .title {
    font-weight: 500;
    font-size: 20px;
    line-height: 24px;
    letter-spacing: -0.24px;
    color: ${Colors.GREY_10};
  }
  .icon > svg > path {
    stroke: ${Colors.GREY_9};
  }
`;

const Content = styled.div<{
  fontSize?: TextSize;
  fontStyle?: string;
  textAlign: string;
  textColor?: string;
  lineHeight?: string;
}>`
  padding-top: 16px;
  color: ${(props) => props?.textColor};
  max-height: 70vh;
  overflow: auto;
  overflow-wrap: break-word;
  text-align: ${(props) => props.textAlign.toLowerCase()};
  font-style: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.ITALIC) ? "italic" : ""};
  text-decoration: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.UNDERLINE) ? "underline" : ""};
  font-weight: ${(props) =>
    props?.fontStyle?.includes(FontStyleTypes.BOLD) ? "bold" : "normal"};
  font-size: ${({ fontSize }) =>
    fontSizeUtility(fontSize) || DEFAULT_FONT_SIZE};
`;
export interface TextComponentProps extends ComponentProps {
  accentColor: string;
  text?: string;
  textAlign: TextAlign;
  ellipsize?: boolean;
  fontSize?: TextSize;
  lineHeight?: string;
  fontFamily: string;
  isLoading: boolean;
  backgroundColor?: string;
  textColor?: string;
  fontStyle?: string;
  truncateButtonColor?: string;
  borderColor?: Color;
  borderWidth?: number;
  overflow: OverflowTypes;
  // helpers to detect and re-calculate content width
  bottomRow?: number;
  leftColumn?: number;
  rightColumn?: number;
  topRow?: number;
  //onClick?: () => void;
  textType: string;
  dataFormat?: string;
  isShowDate?: Boolean;
  dateType?: string;
  isShowTime?: Boolean;
  timeType?: string;
  isShowWeek?: Boolean;
  weekType?: string;
  sortType?: string;
}

type State = {
  isTruncated: boolean;
  showModal: boolean;
};

type TextRef = React.Ref<Text> | undefined;

class TextComponent extends React.Component<TextComponentProps, State> {
  timerID: NodeJS.Timeout | undefined;
  state = {
    isTruncated: false,
    showModal: false,
    date: new Date(),
  };

  textRef = React.createRef() as TextRef;

  // getTruncate = (element: any) => {
  //   const { isTruncated } = this.state;
  //   // add ELLIPSIS_HEIGHT and check content content is overflowing or not
  //   return (
  //     element.scrollHeight >
  //     element.offsetHeight + (isTruncated ? ELLIPSIS_HEIGHT : 0)
  //   );
  // };

  componentDidMount() {
    this.timerID = setInterval(
      () => this.tick(),
      1000
    );
  }

  componentWillUnmount() {
    clearInterval(this.timerID);
  }

  tick() {
    this.setState({
      date: new Date()
    });
  }

  getWeekday = (type: string) => {
    const weekDays = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
    const weekDays1 = ['周日', '周一', '周二', '周三', '周四', '周五', '周六'];
    const date = new Date();
    const weekday = date.getDay();
    return type == "星期二" ? weekDays[weekday] : weekDays1[weekday];
  }

  formatDate = (date: any, format: any, isShowDate: boolean, isShowTime: boolean, isShowWeek: boolean) => {
    const year = date.getFullYear();
    const month = ('0' + (date.getMonth() + 1)).slice(-2);
    const day = ('0' + date.getDate()).slice(-2);
    const hours = ('0' + date.getHours()).slice(-2);
    const minutes = ('0' + date.getMinutes()).slice(-2);
    const seconds = ('0' + date.getSeconds()).slice(-2);
    const { dateType, timeType, sortType, weekType } = format;
    let sDate, sTime, sWeek = '';
    switch (dateType) {
      case '2024-12-10':
        sDate = `${year}-${month}-${day}`;
        break;
      case '2024/12/10':
        sDate = `${year}/${month}/${day}`;
        break;
      case '2024.12.10':
        sDate = `${year}.${month}.${day}`;
        break;
      case '2024年12月10日':
        sDate = `${year}年${month}月${day}日`;
        break;
      case '10-12-2024':
        sDate = `${day}-${month}-${year}`;
        break;
      case '10/12/2024':
        sDate = `${day}/${month}/${year}`;
        break;
      case '10.12.2024':
        sDate = `${day}.${month}.${year}`;
        break;
      default:
        sDate = `${year}年${month}月${day}日`;
        break
    }

    switch (timeType) {
      case '15:31:30':
        sTime = `${hours}:${minutes}:${seconds}`;
        break;
      case '15:31':
        sTime = `${hours}:${minutes}`;
        break;
      default:
        sTime = `${hours}:${minutes}:${seconds}`;
        break
    }

    sWeek = this.getWeekday(weekType)

    sDate = isShowDate ? sDate : "";
    sTime = isShowTime ? sTime : "";
    sWeek = isShowWeek ? sWeek : "";
    let resultDate: any = '';
    switch (sortType) {
      case '日期星期时间':
        resultDate = `${sDate}  ${sWeek}  ${sTime}`;
        break;
      case '日期时间星期':
        resultDate = `${sDate}  ${sTime}  ${sWeek}`;
        break;
      case '星期日期时间':
        resultDate = `${sWeek}  ${sDate}  ${sTime}`;;
        break;
      default:
        resultDate = `${sDate}  ${sWeek}  ${sTime}`;
        break
    }

    return resultDate;

  }
  componentDidUpdate = (prevProps: TextComponentProps) => {

  };


  render() {
    const {
      accentColor,
      backgroundColor,
      ellipsize,
      fontFamily,
      fontSize,
      fontStyle,
      overflow,
      text,
      textAlign,
      textColor,
      truncateButtonColor,
      lineHeight,
      textType,
      dataFormat,
      isShowDate,
      dateType,
      isShowTime,
      timeType,
      isShowWeek,
      weekType,
      sortType
    } = this.props;
    const { date } = this.state;
    let format: any = { dateType: dateType, timeType: timeType, sortType: sortType, weekType: weekType }
    let textFormat: any = this.formatDate(date, format, isShowDate, isShowTime, isShowWeek);  // 1970-01-01 08:00:00
    return (
      <>
        <TextContainer
          fontFamily={fontFamily !== "System Default" ? fontFamily : undefined}
        >
          <StyledText
            backgroundColor={backgroundColor}
            className={this.props.isLoading ? "bp3-skeleton" : "bp3-ui-text"}
            ellipsize={ellipsize}
            fontSize={fontSize}
            lineHeight={lineHeight}
            fontStyle={fontStyle}
            isTruncated={this.state.isTruncated}
            overflow={overflow}
            ref={this.textRef}
            textAlign={textAlign}
            textColor={textColor}
          >
            <Interweave
              content={textFormat}
              matchers={[]

              }
              newWindow
            />
          </StyledText>

        </TextContainer>

      </>
    );
  }
}

export default TextComponent;
